/**
 * Clinical: the encounter shell, schema-driven protocol authoring and
 * referrals.
 *
 * The largest surface because it carries the most refusals. Each conflict the
 * API can answer with needs its own truthful sentence — a single generic
 * "something went wrong" is what makes a clinician reload instead of act.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const clinical = {
  'clinical.parameters.title': "Encounter parameters",
  'clinical.parameters.unset': "Not selected",
  'clinical.parameters.ruPlace': "Place of care",
  'clinical.parameters.ruForm': "Form of care",
  'clinical.parameters.ruType': "Type of medical care",
  'clinical.parameters.ruVisitKind': "Initial or follow-up visit",
  'clinical.parameters.ruCircumstances': "Circumstances of the visit",
  'clinical.parameters.ilSetting': "Care setting",
  'clinical.parameters.ilContact': "Contact type (optional)",
  'clinical.parameters.ilPriority': "Priority (optional)",
  'clinical.parameters.ilService': "Service type (optional)",
  'clinical.parameters.ruHint': "These facts belong to the encounter and apply to all its protocols. Select every field before completing documentation. Patient condition and outcome are recorded in the protocol.",
  'clinical.parameters.ilHint': "Select a care setting. Contact type, priority and service type are optional. These parameters belong to the encounter and apply to all its protocols.",
  'clinical.parameters.suggested': "The care or service type was filled from your last confirmed choice for this protocol. Review it before saving.",
  'clinical.parameters.unbound': "No compatible terminology source is configured for this field. Contact your administrator.",
  'clinical.parameters.noProfile': "Encounter parameters are unavailable: select a supported national terminology profile for the clinic.",
  'clinical.parameters.loading': "Loading encounter parameters…",
  'clinical.parameters.loadFailed': "Encounter parameters could not be loaded.",
  'clinical.parameters.unsaved': "Unsaved encounter parameters",
  'clinical.parameters.save': "Save parameters",
  'clinical.parameters.saving': "Saving parameters…",
  'clinical.parameters.saved': "Encounter parameters saved.",
  'clinical.parameters.saveFailed': "Parameters could not be saved. Your changes are retained.",
  'clinical.parameters.invalid': "Check the selected parameter values. Your changes are retained.",
  'clinical.parameters.required': "Complete and save the encounter parameters before completing documentation.",
  'clinical.parameters.changed': "Encounter parameters changed elsewhere. Reload the stored version before saving.",
  'clinical.parameters.profileChanged': "The national terminology profile changed. Reload the stored parameters and review the selections.",
  'clinical.parameters.readOnly': "Encounter parameters are read-only.",
  'clinical.parameters.resetPreference': "Forget remembered care or service type",
  'clinical.parameters.resetFailed': "The remembered choice could not be cleared.",
  'clinical.parameters.preferenceReset': "Remembered choice cleared. Current encounter parameters are retained.",
  'clinical.parameters.reload': "Load stored parameters",
  'clinical.parameters.reloadFailed': "Stored parameters could not be loaded. Your changes are retained.",
  'clinical.parameters.reloaded': "The form now shows the stored parameters.",
  'clinical.startVisit': 'Start encounter',
  'clinical.startingVisit': 'Starting',
  'clinical.openVisit': 'Open encounter',
  'clinical.openingVisit': 'Opening',
  'clinical.notStartable': 'Not startable',
  'clinical.startMissingCapability': 'No encounter access',
  'clinical.startForbidden': 'You do not have permission to start this encounter.',
  'clinical.startStale': 'This appointment is no longer available.',
  'clinical.alreadyStartedMissingLink': 'This encounter has started, but its link is not available.',
  'clinical.startConflict': 'This appointment is already linked differently. Refresh the schedule.',
  'clinical.startInvalid': 'The encounter could not be started from this appointment.',
  'clinical.startMalformed': 'The encounter response was incomplete.',
  'clinical.startUnavailable': 'The encounter could not be opened right now. Try again.',
  'clinical.startFailed': 'The encounter could not be opened.',
  'clinical.encounterTitle': 'Encounter',
  'clinical.loading': 'Loading encounter',
  'clinical.practitionerRequired': 'Clinical account required',
  'clinical.practitionerRequiredDetail': 'This encounter can be opened only by an account linked to a practitioner.',
  'clinical.practitioner': 'Practitioner',
  'clinical.appointmentTime': 'Appointment time',
  'clinical.caseCode': 'Case code',
  'start.title': 'Start encounter',
  'start.detail':
    'Choose which case this encounter belongs to, or open a new one. Every encounter is filed under a case.',
  'start.promptLabel': 'Start encounter for {name}',
  'start.chooseCase': 'Case for this encounter',
  'start.caseNoneOpen': 'This patient has no open case, so this encounter starts a new one.',
  'start.caseChoose': 'Choose the case this encounter belongs to.',
  'start.confirm': 'Start',
  'start.starting': 'Starting…',
  'start.cancel': 'Cancel',
  /* The Case question both the add-EMR-entry panel and the start-encounter
     prompt ask through `shared/clinical/caseChoice.tsx`. What is being filed
     — an entry or an encounter — is named by the caller's own three
     sentences; everything else about the question is said here, once. */
  'caseChoice.new': 'Create a new case',
  // Stated OUT LOUD when the screen answered for the clinician, so a
  // preselection is never silent.
  'caseChoice.preselected': 'The only open case is selected.',
  'caseChoice.titleLabel': 'Case title',
  'caseChoice.commentLabel': 'Comment',
  'caseChoice.startLabel': 'Start date',
  'caseChoice.endLabel': 'End date',
  'caseChoice.titleRequired': 'A case title is required.',
  'caseChoice.ineligibleHidden':
    '{count} completed or cancelled case(s) are not listed: a closed case cannot take a new encounter.',
  'clinical.status.inProgress': 'In progress',
  /* The word the Complete EMR entry action produces. One state, one word: an
     encounter captioned "Finished" here and "Completed" in the journal is the
     same fact captioned twice, and a clinician has no way to tell that it is
     the same fact. */
  'clinical.status.finished': 'Completed',
  'clinical.status.enteredInError': 'Entered in error',
  'clinical.invalidDeepLink': 'Encounter link is not valid',
  'clinical.invalidDeepLinkDetail': 'Open the encounter from the schedule again.',
  'clinical.staleEvent': 'Encounter link is stale',
  'clinical.staleEventDetail': 'Refresh the schedule and open the encounter again.',
  'clinical.encounterNotFound': 'Encounter not found',
  'clinical.encounterNotFoundDetail': 'This encounter is not available in your clinic.',
  'clinical.sessionExpiredDetail': 'Sign in again to continue.',
  'clinical.unavailable': 'The page could not be loaded',
  'clinical.unavailableDetail': 'Check the connection and try again.',
  'clinical.invalidResponse': 'The server response was incomplete',
  'clinical.invalidResponseDetail': 'Try again, or ask an administrator to check Core.',

  'record.title': 'Clinical note',
  'record.detailTitle': 'Clinical note detail',
  'record.loading': 'Loading the clinical note…',
  'record.loadFailed': 'The clinical note could not be loaded.',
  'record.forbidden': 'You do not have permission to author clinical notes.',
  'record.sessionEnded': 'Your session ended. Sign in again to continue.',
  'record.gone': 'This clinical note is no longer available.',
  'record.invalidInput': 'The server rejected this note. Check the fields and try again.',
  'record.saveFailed': 'The note could not be saved. Try again.',
  'record.workplaceRequired': 'A workplace must be selected for this document.',
  'record.workplaceUnavailable': 'This document\'s workplace is no longer available for writing. Its author context must be updated before continuing.',
  'record.authorWorkplace': 'Author workplace',
  'record.changeWorkplace': 'Change author workplace',
  'record.workplaceNotRecorded': 'Not recorded',
  'record.workplaceUnsavedIntent': 'Clinical document author workplace',
  'correction.unsavedIntent': 'Clinical document correction reason and workplace',
  'record.workplaceProtocolIncompatible': 'The selected workplace requires a different protocol. The document and your entries are unchanged.',
  'record.noRelease': 'No published protocol is available.',
  'record.noReleaseDetail': 'A clinical protocol must be published before notes can be written.',
  'record.invalidSchema':
    'The published protocol cannot be displayed by this version of the application.',
  'record.unsupportedField': 'This field type is not supported by this version',
  'record.fieldRequired': 'This field is required before the note can be finalized.',
  'record.codedSearch': 'Search the terminology dictionary',
  'record.codedClear': 'Clear coded value',
  'record.choiceUnanswered': 'Not answered',
  'record.codedUnavailable': 'The dictionary is unavailable. The current answer is kept.',
  'record.referenceNone': 'No service selected',
  'record.referenceUnavailable': 'Clinic services are unavailable. Your current selection is kept.',
  'record.create': 'Create note',
  'record.save': 'Save',
  'record.conflict':
    'This note was changed elsewhere. Load the saved revision to continue; your unsaved edits will be discarded.',
  'record.reloadServerVersion': 'Load the saved revision',
  'record.stateNew': 'Not created',
  'record.stateSaved': 'Saved',
  'record.stateUnsaved': 'Unsaved changes',
  'record.unsavedIntent': 'Clinical note — edits that were never saved',
  'record.primaryUnsavedIntent': 'Primary protocol — edits that were never saved',
  'record.primaryTranslationUnsavedIntent': 'Primary protocol translation — edits that were never saved',
  'record.stateSaving': 'Saving…',
  'record.stateFinal': 'Final',
  'record.printLoading': 'Loading print forms',
  'record.printPdf': 'Print PDF',
  'record.printLanguage': 'Print language',
  'record.printingPdf': 'Preparing PDF…',
  'record.printDownloaded': 'PDF downloaded.',
  'record.printFailed': 'The PDF could not be prepared. Try again.',
  'record.printForbidden': 'You do not have permission to print this note.',
  'record.printUnavailable': 'This note is not ready for printing.',
  'record.printConflict': 'This print request changed while it was being prepared. Try again.',
  'record.dictationOpen': 'Dictate',
  'record.dictationTitle': 'Dictation and AI assistant',
  'record.dictationIntro':
    'Speak through the protocol topics. After you stop, the recording is sent to OpenAI for transcription and field extraction.',
  'record.dictationTopics': 'Suggested speaking topics, from this note’s protocol:',
  'record.dictationNoTopics': 'This protocol has no fields that can receive dictated text.',
  'record.dictationSourceLanguage': 'Spoken language',
  'record.dictationOutputLanguage': 'Note language',
  'record.dictationLanguageAuto': 'Detect automatically',
  'record.dictationStart': 'Start recording',
  'record.dictationRetry': 'Record again',
  'record.dictationStop': 'Stop',
  'record.dictationTimer': 'Recording time',
  'record.dictationRequesting': 'Requesting microphone access…',
  'record.dictationRecording': 'Recording…',
  'record.dictationProcessing': 'Transcribing and arranging the protocol fields…',
  'record.dictationReady': 'Review the transcription and extracted fields before applying them.',
  'record.dictationTranscript': 'Transcript',
  'record.dictationExtracted': 'Extracted fields',
  'record.dictationNoExtracted': 'No protocol field content was recognized.',
  'record.dictationUnassigned': 'Content not assigned to a field',
  'record.dictationWarnings': 'Warnings',
  'record.dictationApply': 'Apply to draft',
  'record.dictationPermissionDenied': 'Microphone access was denied. Allow access and try again.',
  'record.dictationMicrophoneFailed': 'The microphone could not be started.',
  'record.dictationUnsupportedBrowser': 'This browser cannot record audio for dictation.',
  'record.dictationKeyMissing': 'Add your OpenAI API key in Profile settings before using dictation.',
  'record.dictationSchemaChanged': 'The protocol changed. Close this dialog and reload the note.',
  'record.dictationTooLarge': 'The recording is larger than {max} MB. Record a shorter protocol.',
  'record.dictationUnsupportedAudio': 'This browser produced an unsupported audio format.',
  'record.dictationForbidden': 'You do not have permission to use dictation for this note.',
  'record.dictationFailed': 'The recording could not be processed. Try again.',
  'record.dictationClose': 'Close',
  'correction.alreadyCorrected':
    'This note has already been replaced by someone else. Open the correction history to read the replacement — your change was not applied.',
  'correction.idempotencyConflict':
    'This correction was already submitted with different details. Nothing was replaced. Start the correction again rather than resending.',
  'correction.changedConcurrently':
    'This note changed while you were reading it. Reload it before correcting.',
  'correction.forbidden': 'You do not have permission to correct this note.',
  'correction.gone': 'This note is no longer available.',
  'correction.invalidInput': 'Check the reason for the correction.',
  'correction.failed': 'The correction could not be completed.',
  'correction.historyLoading': 'Checking correction history',
  'correction.historyUnavailable': 'The correction history could not be read.',
  'correction.wasReplaced': 'This note was replaced',
  'correction.replacesAnother': 'This note replaces an earlier one',
  'correction.reason': 'Reason for the correction',
  'correction.correctedAt': 'Corrected',
  'correction.openReplacement': 'Open the replacement',
  'correction.openOriginal': 'Open the replaced note',
  'correction.amend': 'Correct entry',
  'correction.markEnteredInError': 'Mark as entered in error',
  'correction.showHistory': 'History',
  'correction.amendTitle': 'Correct this protocol',
  'correction.amendBody': 'Choose the reason. The current document stays unchanged as superseded, and a new editable correction starts with the same content.',
  'correction.enteredErrorTitle': 'Mark this protocol as entered in error',
  'correction.enteredErrorBody': 'Choose the reason. The document will no longer be current and no replacement will be created.',
  'correction.amending': 'Creating correction…',
  'correction.markingEnteredError': 'Marking as entered in error…',
  'correction.reasonClinicalError': 'Clinical correction',
  'correction.reasonWrongPatient': 'Wrong patient',
  'correction.reasonWrongProtocol': 'Wrong protocol',
  'correction.reasonMissingInformation': 'Missing information',
  'correction.reasonDuplicateEntry': 'Duplicate protocol',
  // The reason set is closed by the storage contract; "other" records the
  // clinician's own words as required detail under the clinical-error code.
  'correction.reasonOther': 'Other reason',
  'correction.reasonDetail': 'Reason in your own words',
  'correction.historyTitle': 'Protocol history',
  'correction.historyEmpty': 'No archived revisions.',
  'correction.historyEnteredError': 'Entered in error',
  'correction.historyAmended': 'Corrected with a replacement',
  'correction.confirmAmendment': 'Confirm correction',
  'correction.cancel': 'Keep the note as it is',
  // The read-only document. An empty field is NAMED rather than left blank,
  // so a reader can tell "nothing was written here" from "this did not load".
  'record.fieldEmpty': 'Not recorded',
  'record.unrepresentedTitle': 'Content this version cannot display',
  'record.unrepresentedDetail':
    'The stored protocol carries these values, and this application does not know where to place them. They are listed so nothing is silently dropped.',
  'record.draftNotice': 'This is a draft. It is not signed clinical evidence.',
  'record.evidence': 'Finalization',
  'record.finalizedAt': 'Finalized',
  'record.protocol': 'Protocol',
  'record.protocolLoading': 'Loading protocol…',
  'record.protocolUnavailable': 'Protocol name unavailable',
  'record.status': 'Record status',

  /* ---------------------------------------------------------------------
     COMPLETE EMR ENTRY. One action, one server producer: it requires a main
     diagnosis, finalizes every current protocol, closes the Encounter and
     completes the linked appointment in one transaction. Each conflict the
     producer can raise needs a DIFFERENT next step from the clinician, so each
     gets its own sentence keyed on the machine code. */
  'clinical.completeEntry': 'Complete EMR entry',
  'clinical.completing': 'Completing…',
  'clinical.completeConfirmTitle': 'Confirm completing the EMR entry',
  'clinical.completeConfirmBody':
    'Completing confirms every current protocol, closes this encounter and its appointment. The clinical encounter will not be reopened.',
  'clinical.completeConfirm': 'Complete permanently',
  'clinical.completeCorrection': 'Complete correction',
  'clinical.completingCorrection': 'Completing correction…',
  'clinical.completeCorrectionConfirmTitle': 'Confirm completing the correction',
  'clinical.completeCorrectionConfirmBody':
    'Completing confirms every current protocol and closes the correction workspace. The completed encounter and appointment stay unchanged.',
  'clinical.completeCorrectionConfirm': 'Complete correction',
  'clinical.completeCancel': 'Keep the entry open',
  // The generic conflict is NOT dead: it covers a code this build has never
  // heard of, which is what a newer server would send. Naming a cause there
  // would be wrong exactly when the cause is unknown.
  'clinical.completeConflict':
    'This EMR entry could not be completed because its clinical state changed. Reload to see its current state.',
  'clinical.completeForbidden': 'You do not have permission to complete EMR entries.',
  'clinical.completeSessionEnded': 'Your session ended. Sign in again to continue.',
  'clinical.completeGone': 'This EMR entry is no longer available.',
  'clinical.completeUnavailable': 'The service is unavailable. Try again shortly.',
  'clinical.completeFailed': 'The EMR entry could not be completed. Try again.',
  'clinical.completeAlreadyFinished':
    'This EMR entry has already been completed. Reload to see its current state.',
  // The diagnosis requirement is stated HERE, in the completion refusal, and
  // again in the Diagnoses section. It is never buried in protocol text.
  'clinical.completeMainDiagnosisRequired':
    'This entry has no main diagnosis. Record one in the Diagnoses section, then complete the entry.',
  'clinical.completeProtocolInvalid':
    'One or more protocols cannot be confirmed as they stand. Complete their required fields, then try again.',
  'clinical.completeSaveProtocolsFirst': 'Save all protocol changes before completing.',
  'clinical.completeEnteredInError':
    'This EMR entry was marked entered in error and cannot be completed.',
  'clinical.completeChangedConcurrently':
    'This EMR entry changed elsewhere while this screen was open. Reload to see its current state.',
  'clinical.completeNotFinishable':
    'This EMR entry cannot be completed in its current state. Reload to see it.',
  'clinical.completeScheduleNotCloseable':
    'The linked appointment could not be closed, so nothing was completed. Check the appointment, then try again.',
  'clinical.completeCorrectionNotOpen':
    'There is no open correction to complete. Reload to see the current documentation state.',

  'clinical.sectionDiagnoses': 'Diagnoses',
  'clinical.entryCompleted': 'The visit and its documentation are completed. Protocols are read-only until a correction is started.',
  'clinical.entryCorrectionOpen':
    'A documentation correction is in progress. The clinical encounter and appointment remain completed.',
  'clinical.entryEnteredInError': 'This EMR entry is marked entered in error and is read-only.',
  'clinical.encounterStarted': 'Started',
  'clinical.encounterType': 'Entry type',
  'clinical.typeScheduled': 'Scheduled encounter',
  'clinical.typeUnscheduled': 'Unscheduled encounter',

  /* The entry's protocols. The words name a LIST rather than a note: an entry
     carries one note per published protocol, and each is its own tile in the
     panel. */
  'encounterProtocols.title': 'Protocols',
  'encounterProtocols.forbidden': 'You do not have permission to read clinical notes.',
  'encounterProtocols.statusDraft': 'Draft',
  'encounterProtocols.statusFinal': 'Final',
  'encounterProtocols.statusAmended': 'Amended',
  'encounterProtocols.statusEnteredInError': 'Entered in error',

  /* The entry's share of the navigation panel. */
  'encounterNav.entryLabel': 'This EMR entry',
  /* Names the lone mono chip under the patient plate for a screen reader. The
     patient's own code is on the plate and carries the plate's label. */
  'encounterNav.entryCodeLabel': 'EMR entry code',
  'encounterNav.menuLabel': 'EMR entry sections',
  'encounterNav.addDescription': 'Start another note on this entry',
  'encounterNav.diagnosesDescription': 'What this entry concluded',
  'encounterNav.referrals': 'Referrals',
  'encounterNav.referralsDescription': 'What was sent onward from here',
  'encounterNav.services': 'Services',
  'encounterNav.servicesDescription': 'Performed services and their charges',
  'encounterNav.protocolHistoryDescription': 'Archived protocol revisions',
  'encounterNav.protocolHistoryMeta': 'History',
  /* The print group at the panel's foot: one button per protocol, each of them
     the product's single print mechanism — a PDF rendered from the bound
     print-form release and saved as a file. */
  'encounterNav.printGroupLabel': 'Print PDF',

  'addProtocol.title': 'Add protocol',
  'addProtocol.detail': 'A new note is created empty and saved. Nothing is finalized here.',
  'addProtocol.assignmentInactive': "The selected protocol is unavailable for new records. Choose another protocol.",
  'addProtocol.legend': 'Which protocol',
  'addProtocol.submit': 'Start protocol',
  'addProtocol.submitting': 'Starting…',
  'addProtocol.alreadyStarted':
    'This protocol was already started on this entry. Close this panel to see it.',

  'referral.title': 'Referral',
  'referral.sectionTitle': 'Referrals',
  'referral.emptyEncounter': 'No referrals on this entry yet.',
  'referral.emptyEncounterDetail':
    'A referral is raised from a finalized consultation note. Finalize the note, then return here.',
  'referral.otherGroup': 'Referrals from notes not shown above',
  'referral.detailTitle': 'Referral detail',
  'referral.loading': 'Loading referrals…',
  'referral.loadFailed': 'Referrals could not be loaded.',
  'referral.forbidden': 'You do not have permission to work with referrals.',
  'referral.notFound': 'This referral is not available.',
  'referral.invalidInput': 'The server rejected this referral. Check the fields and try again.',
  'referral.saveFailed': 'The referral could not be saved. Try again.',
  'referral.conflict':
    'This referral could not be saved because it changed. Reload to see its current state.',
  'referral.changedConcurrently':
    'This referral was changed elsewhere. Reload to see the saved revision before continuing.',
  'referral.sourceMismatch':
    'This referral does not belong to the note shown here. Reload before continuing.',
  'referral.priority': 'Priority',
  'referral.priorityRoutine': 'Routine',
  'referral.priorityUrgent': 'Urgent',
  // Not "Specialty code": the clinician now chooses a published specialty by
  // its name, and the code is an identifier they never see or type.
  'referral.specialty': 'Specialty',
  'referral.specialtyChoose': 'Choose a specialty',
  'referral.specialtiesLoading': 'Loading the specialties this referral accepts…',
  'referral.specialtiesUnavailable':
    'The specialties this referral accepts could not be loaded, so a referral cannot be created here yet.',
  'referral.recipient': 'Refer to',
  'referral.reason': 'Reason for referral',
  'referral.note': 'Note (optional)',
  'referral.specialtyRequired': 'A specialty is required.',
  'referral.recipientRequired': 'A recipient is required.',
  'referral.reasonRequired': 'A reason for the referral is required.',
  'referral.create': 'Create referral',
  'referral.newReferral': 'New referral',
  'referral.cancelEditor': 'Cancel',
  'referral.created': 'Referral {code} was created.',
  'referral.save': 'Save referral',
  'referral.editDraft': 'Edit draft',
  'referral.activate': 'Activate referral',
  'referral.stateDraft': 'Draft',
  'referral.stateActive': 'Active',
  'referral.stateEnteredInError': 'Entered in error',
  'referral.activeImmutable': 'This referral is active and can no longer be changed.',
  'referral.status': 'Referral status',
  'referral.authoredAt': 'Authored',
  'referral.activatedAt': 'Activated',
  'referral.sourceRecord': 'Source note',
  'referral.sourceWorkplace': 'From the source note',
  'addProtocol.inheritedWorkplace': 'From the clinical entry',

  // Scoped to the SOURCE RELATION. The referral itself loaded; only the note
  // it points at did not, and turning the page into a not-found would send the
  // clinician looking for a referral that is right in front of them.
  'referral.sourceUnavailable': 'The note this referral was raised from is unavailable.',
  'referral.sourceNotFinal':
    'This note is no longer final, so a referral cannot be raised from it. Reload to see its current state.',
  'referral.sourceUnsupported': 'A referral cannot be raised from this type of note.',
  // Names the field and the fact. The old fallback said the referral had
  // changed and to reload, which could never help: the value was refused, and
  // nothing about the referral changed.
  'referral.specialtyUnsupported':
    'That specialty is not accepted for this referral type. Use one the referral definition allows.',

  'referral.discard': 'Discard draft',
  'referral.discardConfirmBody':
    'Discarding withdraws this draft referral. It is kept as a withdrawn record with the reason, not deleted.',
  'referral.discardReason': 'Reason for discarding',
  'referral.discardReasonRequired': 'A reason is required before this draft can be discarded.',
  'referral.discardConfirm': 'Discard permanently',
  'referral.discardCancel': 'Keep the draft',
  'referral.unsavedIntent': 'Referral — details that were never saved',
  'referral.unsavedDiscardReason': 'Referral withdrawal — a reason that was never sent',
  // A same-key retry carrying different content. Retrying cannot help and the
  // clinician needs to know their new text was not what was recorded.
  'referral.idempotencyConflict':
    'This withdrawal was already recorded with a different reason. Reload to see what was saved.',
  'referral.notDraft':
    'This referral has been activated and can no longer be changed. Reload to see it.',
  /* ---------------------------------------------------------------------
     THE ENCOUNTER DIAGNOSIS CONTEXT.

     The context is the SERVER's aggregate: it owns the revision, decides what
     `current` means and enforces at most one visible, non-removed, non-refuted
     main diagnosis. Nothing here re-derives any of that. The requirement that a
     completed entry carries a main diagnosis is stated in the open, on this
     section, rather than hidden inside protocol text.

     Diagnosis coding is selected from the clinic terminology exposed through
     Core's Terminology consumer routes. The browser never offers manual coding
     fields or an invented local dictionary. */
  'diagnosis.title': 'Diagnoses',
  'diagnosis.loading': 'Loading diagnoses…',
  'diagnosis.loadFailed': 'The diagnosis context could not be loaded.',
  'diagnosis.forbidden': 'You do not have permission to read diagnoses.',
  'diagnosis.empty': 'No diagnoses recorded yet',
  'diagnosis.emptyDetail': 'Record the main diagnosis for this encounter first.',
  'diagnosis.mainRequired':
    'This EMR entry has no main diagnosis. One is required before it can be completed.',
  'diagnosis.mainRecorded': 'A main diagnosis is recorded for this EMR entry.',
  'diagnosis.roleMain': 'Main',
  'diagnosis.roleComplication': 'Complication',
  'diagnosis.roleConcomitant': 'Concomitant',
  'diagnosis.roleAdditional': 'Additional',
  'diagnosis.clinicalActive': 'Active',
  'diagnosis.clinicalInactive': 'Inactive',
  'diagnosis.clinicalResolved': 'Resolved',
  'diagnosis.verificationConfirmed': 'Confirmed',
  'diagnosis.verificationProvisional': 'Provisional',
  'diagnosis.verificationRefuted': 'Refuted',
  'diagnosis.verificationEnteredInError': 'Entered in error',
  // The server drops removed and entered-in-error threads from the current view
  // and keeps refuted ones, so REFUTED is a state a row can be read in. It is
  // marked with its verification status above rather than hidden, because a
  // refuted main diagnosis is exactly the case where "there is a main
  // diagnosis" and "this entry can be completed" disagree.
  // A merged row survived a merge decision. The mark says which command put it
  // in its current shape; it is not a status and does not change what it means.
  'diagnosis.mergedMark': 'Merged',
  'diagnosis.listLabel': 'Recorded diagnoses',
  'diagnosis.onset': 'Onset',
  'diagnosis.recordedAt': 'Recorded',
  'diagnosis.note': 'Note',
  // The visible face is the uniform verb, like Edit/Remove on the rows; the
  // role lives in the accessible name and in the band title beside the button,
  // so four sections stay tellable apart without four widths of button.
  'diagnosis.add': 'Add',
  'diagnosis.addMain': 'Add main diagnosis',
  'diagnosis.addComplication': 'Add complication',
  'diagnosis.addConcomitant': 'Add concomitant diagnosis',
  'diagnosis.addAdditional': 'Add additional diagnosis',
  'diagnosis.addTitle': 'New diagnosis',
  'diagnosis.editTitle': 'Edit diagnosis',
  'diagnosis.edit': 'Edit',
  'diagnosis.editNamed': 'Edit {name}',
  'diagnosis.remove': 'Remove',
  'diagnosis.removeNamed': 'Remove {name}',
  'diagnosis.fieldRole': 'Role',
  'diagnosis.fieldDisplay': 'Coded diagnosis',
  'diagnosis.searchPlaceholder': 'Search by code or diagnosis name',
  'diagnosis.searchHint': 'Type at least two characters to search.',
  'diagnosis.searching': 'Searching diagnoses…',
  'diagnosis.searchFailed': 'The diagnosis terminology is unavailable.',
  'diagnosis.searchEmpty': 'No diagnosis matches this search.',
  'diagnosis.searchResults': 'Diagnosis search results',
  'diagnosis.fieldOnset': 'Onset date',
  'diagnosis.fieldNote': 'Note',
  'diagnosis.fieldClinicalStatus': 'Clinical status',
  'diagnosis.fieldVerification': 'Verification',
  'diagnosis.attributesTitle': 'Diagnosis details',
  'diagnosis.fieldDiagnosisKind': 'Diagnosis kind',
  'diagnosis.fieldDiseaseNature': 'Disease nature',
  'diagnosis.fieldBicausalType': 'Additional type',
  'diagnosis.fieldClinicalGroup': 'Clinical group',
  'diagnosis.fieldTopography': 'ICD-O-3 topography',
  'diagnosis.fieldMorphology': 'ICD-O-3 morphology',
  'diagnosis.fieldTNM': 'TNM',
  'diagnosis.fieldRecurrence': 'Recurrence',
  'diagnosis.fieldProgression': 'Progression',
  'diagnosis.selectPlaceholder': 'Select',
  'diagnosis.kindPreliminary': 'Preliminary',
  'diagnosis.kindClinical': 'Clinical',
  'diagnosis.kindFinal': 'Final',
  'diagnosis.natureAcute': 'Acute',
  'diagnosis.natureChronicFirst': 'Chronic, first detected',
  'diagnosis.natureChronicEstablished': 'Chronic, previously established',
  'diagnosis.groupI': 'I',
  'diagnosis.groupIA': 'IA',
  'diagnosis.groupIB': 'IB',
  'diagnosis.groupIPlus': 'I+',
  'diagnosis.groupII': 'II',
  'diagnosis.groupIIA': 'IIA',
  'diagnosis.groupIII': 'III',
  'diagnosis.groupIV': 'IV',
  'diagnosis.bicausalCompeting': 'Competing',
  'diagnosis.bicausalCombined': 'Combined',
  'diagnosis.bicausalBackground': 'Background',
  'diagnosis.oncologyTitle': 'Oncology',
  'diagnosis.oncologyCategoryMalignant': 'Malignant neoplasm',
  'diagnosis.oncologyCategoryBenign': 'Benign neoplasm',
  'diagnosis.oncologyCategoryInSitu': 'In situ neoplasm',
  'diagnosis.oncologyCategoryUncertain': 'Uncertain behavior',
  'diagnosis.oncologyCategoryUnspecified': 'Unspecified nature',
  'diagnosis.oncologyFlags': 'Oncology flags',
  'diagnosis.topographyAuto': 'Derived from the diagnosis code.',
  'diagnosis.kindRequired': 'Choose the diagnosis kind.',
  'diagnosis.natureRequired': 'Choose the disease nature.',
  'diagnosis.bicausalRequired': 'Choose the additional diagnosis type.',
  'diagnosis.clinicalGroupRequired': 'Choose the clinical group.',
  'diagnosis.topographyRequired': 'Choose ICD-O-3 topography.',
  'diagnosis.morphologyRequired': 'Choose ICD-O-3 morphology.',
  'diagnosis.oncologyChecking': 'Checking oncology requirements…',
  'diagnosis.oncologyUnavailable': 'Oncology terminology is unavailable.',
  'diagnosis.tnmChecking': 'Loading TNM options…',
  'diagnosis.tnmUnavailable': 'TNM options are unavailable.',
  'diagnosis.tnmRequired': 'Choose TNM.',
  'diagnosis.selectionRequired': 'Choose a diagnosis from the clinic terminology.',
  'diagnosis.save': 'Save diagnosis',
  'diagnosis.saving': 'Saving…',
  'diagnosis.cancel': 'Cancel',
  'diagnosis.unsavedIntent': 'Diagnosis \u2014 edits that were never saved',
  // The stale bar. `stale` is the server's verdict that the context no longer
  // matches what it was accepted against; the three answers are the producer's
  // three routes, and this screen offers all three rather than picking one.
  /* The REVIEW decisions. Accept, confirm and merge are three different
     statements about the same context, so they are three controls and not one
     with a mode. A reason is optional on all three because the server accepts
     it that way; requiring one here would invent a rule a clinician can meet
     only by typing something.

     `stale` is a status the contract defines and no command in this build
     produces. Its banner is kept because a server that starts producing it must
     not meet a screen that renders the word and explains nothing. */
  'diagnosis.reviewTitle': 'Review this context',
  'diagnosis.reviewDetail': 'Records who reviewed the diagnoses and when. It changes no diagnosis.',
  'diagnosis.staleTitle': 'This diagnosis context is out of date',
  'diagnosis.staleDetail':
    'It changed after it was last reviewed. Accept it as it stands, confirm it, or merge the diagnoses into a resolved context.',
  'diagnosis.accept': 'Accept',
  'diagnosis.confirm': 'Confirm',
  'diagnosis.merge': 'Merge',
  'diagnosis.reason': 'Reason (optional)',
  'diagnosis.decisionSubmit': 'Record the decision',
  'diagnosis.decisionCancel': 'Cancel',
  'diagnosis.mergeTitle': 'Merge the diagnosis context',
  /* Says exactly what the producer does. A merge RESTATES the diagnoses it is
     given as the resolved context; it does not delete the ones it is not
     given, and copy that promised deletion would describe a different
     endpoint. Removing a diagnosis is its own action on its own row. */
  'diagnosis.mergeDetail':
    'Restate these diagnoses as the merged context. One left out is not restated and keeps the shape it already has.',
  'diagnosis.mergeRestate': 'Restate {name}',
  'diagnosis.mergeEmpty': 'Choose at least one diagnosis to restate.',
  'diagnosis.mergeSubmit': 'Merge and record',
  'diagnosis.contextStatusCurrent': 'Current',
  'diagnosis.contextStatusStale': 'Out of date',
  'diagnosis.contextStatusAccepted': 'Accepted',
  'diagnosis.contextStatusConfirmed': 'Confirmed',
  'diagnosis.decisionsTitle': 'Review decisions',
  'diagnosis.decisionAccept': 'Accepted',
  'diagnosis.decisionConfirm': 'Confirmed',
  'diagnosis.decisionMerge': 'Merged',
  'diagnosis.readOnly': 'Diagnoses are read-only for this EMR entry.',
  'diagnosis.readOnlyDetail':
    'A completed or entered-in-error encounter keeps its diagnoses readable and refuses changes.',
  /* Each refusal names the act that clears it. A main conflict is the one the
     server enforces with an index, so it gets the sentence that says which of
     the two diagnoses has to change. */
  'diagnosis.mainConflict':
    'This encounter already has a main diagnosis. Change the existing one, or record this as another role.',
  'diagnosis.changedConcurrently':
    'The diagnosis context changed elsewhere while this screen was open. Reload to see its current state.',
  'diagnosis.writeReadOnly': 'This encounter no longer accepts diagnosis changes.',
  'diagnosis.writeForbidden': 'You do not have permission to change diagnoses.',
  'diagnosis.writeSessionEnded': 'Your session ended. Sign in again to continue.',
  'diagnosis.writeGone': 'This encounter is no longer available.',
  'diagnosis.writeUnavailable': 'The service is unavailable. Try again shortly.',
  'diagnosis.writeInvalid': 'Choose the diagnosis from the clinic terminology and try again.',
  'diagnosis.writeAttributesInvalid': 'Complete the required diagnosis details and try again.',
  'diagnosis.writeFailed': 'The diagnosis could not be saved. Try again.',
  'diagnosis.reload': 'Reload the diagnosis context',

  /*
   * The docked dictionary panel.
   *
   * "My codes" rather than "Personal dictionary": the shortlist is the
   * clinician's own and the possessive is what makes that readable at a glance;
   * the other tab is the clinic-selected terminology.
   */
  'diagnosis.dictionaryTitle': 'Diagnosis dictionary',
  'diagnosis.dictionaryTitleTopography': 'ICD-O-3 topography',
  'diagnosis.dictionaryTitleMorphology': 'ICD-O-3 morphology',
  'diagnosis.dictionaryTabCatalogueICD10': 'ICD-10',
  'diagnosis.dictionaryTabCatalogueICD9IL': 'ICD-9 IL',
  'diagnosis.dictionarySearchPlaceholder': 'Code or name',
  'diagnosis.dictionaryTopographyPlaceholder': 'Topography code or site',
  'diagnosis.dictionaryMorphologyPlaceholder': 'Morphology code or histology',
  'diagnosis.dictionarySearchResults': 'Diagnosis search results',
  'diagnosis.dictionaryBrowseHint':
    'Browsing the tree. Type three characters, or a code prefix, to search the whole dictionary.',
  'diagnosis.dictionarySearching': 'Searching the dictionary…',
  'diagnosis.dictionaryLoading': 'Loading…',
  'diagnosis.dictionaryLoadFailed': 'The dictionary could not be read.',
  'diagnosis.dictionaryEmptyLevel': 'Nothing is filed under this code.',
  /* The context menus. The menu itself is named for the thing it acts on, so
     the actions inside it stay short: their subject is already stated. */
  /* The four role sections, and what the badge beside each one counts. */
  'diagnosis.sectionCount': '{count} recorded',
  'diagnosis.sectionEmpty': 'None recorded.',
  'diagnosis.secondaryBlocked': 'Record the main diagnosis first.',
  /*
   * QUICK ENTRIES — the Encounter as a phone offers it (#372).
   *
   * The words of three sheets and of the line that says where the rest is. They
   * are the entry's own vocabulary rather than the record's, because what they
   * name is a different act: `record.*` speaks for the protocol form, which
   * carries a draft, a conflict recovery and finalization, and none of that
   * exists here. What is shared is shared by id — a refused session or a
   * rejected field says exactly what it says on the desktop.
   */
  'encounterQuick.title': 'Quick entry',
  'encounterQuick.deskOnly':
    'This entry is read-only here. The protocol editor, dictation, referrals, services and print forms are on the desktop.',
  'encounterQuick.note': 'Add to the note',
  'encounterQuick.vitals': 'Record vital parameters',
  'encounterQuick.task': 'Create a task',
  'encounterQuick.save': 'Save',
  'encounterQuick.saving': 'Saving…',
  'encounterQuick.saveFailed': 'This could not be saved. Try again.',
  'encounterQuick.saveForbidden': 'You do not have permission to save this.',
  'encounterQuick.saveUnavailable': 'The server did not answer. Try again.',
  'encounterQuick.noteInto': 'Adding to: {protocol}',
  'encounterQuick.noteProtocol': 'Note',
  'encounterQuick.noteChoose': 'Choose a note',
  'encounterQuick.noteNowhere':
    'Every note on this entry is signed. Correcting one, or starting another, is done on the desktop.',
  'encounterQuick.noteNoFreeText':
    'This protocol has no free-text field, so a sentence cannot be added to it here.',
  'encounterQuick.noteConflict':
    'This note changed while the sheet was open. Close it, open the note again and enter the text once more.',
  'encounterQuick.protocolUnwritten': 'This protocol has not been written on this entry yet.',
  'encounterQuick.vitalLoading': 'Loading what can be recorded…',
  'encounterQuick.vitalCatalogueFailed': 'The vital parameters that can be recorded could not be read.',
  'encounterQuick.vitalEmpty': 'No vital parameter can be recorded from this build.',
  'encounterQuick.vitalNothing': 'Enter at least one measurement.',
  'encounterQuick.vitalPartial': 'Recorded whole or not at all: fill in every part of {name}, or clear it.',
  'encounterQuick.vitalNotNumber': 'Not a number: {name}',
  'encounterQuick.vitalRefused': 'The server rejected these measurements. Check them and try again.',
  'encounterQuick.taskNoPractitioner':
    'Your account is not a clinician, so it cannot be made responsible for a task.',
  "record.patientDifference": "Patient details differ from those saved in this protocol.",
  "record.comparePatient": "View differences",
  "record.patientComparisonUnavailable": "The saved and current patient details cannot be compared. The protocol remains available.",
  "record.patientNameThen": "Name in protocol",
  "record.patientNameNow": "Name in current card",
  "record.patientBirthDateThen": "Birth date in protocol (age at visit)",
  "record.patientBirthDateNow": "Birth date in current card (age at visit)",
  "record.patientCorrectionContext": "Correction creates a new revision with the current patient context, including anamnesis. Review it before completing correction; completion confirms the encounter’s current protocol set.",
  "record.keepPatientSnapshot": "Leave as is",
} as const
